/**
 * The three-letter editor, drawn big: the letter under the cursor pulses with ▲ and ▼ beside it,
 * which mouse and touch can press. Used by Initials and by the Daily Dig board.
 */

import { BLACK } from '../../render/palette';
import type { Screen } from '../../render/screen';
import type { InitialsState, InitialsStep } from './initialsModel';
import { cycleLetter } from './initialsModel';
import type { Rect } from './menuModel';
import { inRect } from './menuModel';
import { blink, cycle, FAINT, INK, MUTED, RAMPS, SELECTED } from './theme';

export interface EditorLook {
  /** Centre of the three letters. */
  readonly cx: number;
  /** Top of the letters. */
  readonly y: number;
  /** Glyph scale (2 or 3). */
  readonly scale: number;
}

function slotX(look: EditorLook, i: number): number {
  const w = 8 * look.scale;
  const step = w + 4 * look.scale;
  return Math.round(look.cx - (3 * w + 2 * (step - w)) / 2 + i * step);
}

/** Hit areas of slot `i`: the ▲ above, the letter, the ▼ below. */
export function editorRects(look: EditorLook, i: number): { up: Rect; letter: Rect; down: Rect } {
  const w = 8 * look.scale;
  const x = slotX(look, i) - 2;
  return {
    up: { x, y: look.y - 14, w: w + 4, h: 13 },
    letter: { x, y: look.y - 1, w: w + 4, h: w + 2 },
    down: { x, y: look.y + w + 1, w: w + 4, h: 13 },
  };
}

export type EditorHit = { readonly slot: number; readonly part: 'up' | 'letter' | 'down' } | null;

export function editorHit(look: EditorLook, x: number, y: number): EditorHit {
  for (let i = 0; i < 3; i++) {
    const r = editorRects(look, i);
    if (inRect(r.up, x, y)) return { slot: i, part: 'up' };
    if (inRect(r.letter, x, y)) return { slot: i, part: 'letter' };
    if (inRect(r.down, x, y)) return { slot: i, part: 'down' };
  }
  return null;
}

/** A press or wheel on the editor: pick a letter, or cycle it with its arrows. Null if it missed. */
export function editorPointer(
  s: InitialsState, look: EditorLook, kind: string, x: number, y: number, delta: number,
): InitialsStep | null {
  if (kind === 'wheel') return { state: cycleLetter(s, -delta), sound: 'type', done: false };
  if (kind !== 'down') return null;
  const hit = editorHit(look, x, y);
  if (!hit) return null;
  const at = { ...s, cursor: hit.slot };
  if (hit.part === 'letter') return { state: at, sound: 'move', done: false };
  return { state: cycleLetter(at, hit.part === 'up' ? 1 : -1), sound: 'type', done: false };
}

export function drawEditor(screen: Screen, s: InitialsState, look: EditorLook, ms: number, active = true): void {
  const w = 8 * look.scale;
  for (let i = 0; i < 3; i++) {
    const x = slotX(look, i);
    const on = active && i === s.cursor;
    const ch = s.letters[i] ?? ' ';
    screen.fill(x - 1, look.y + w + 1, w + 2, 2, on ? SELECTED : FAINT);
    if (ch !== ' ') {
      screen.text(ch, x + 1, look.y + 1, BLACK, 'left', look.scale);
      screen.text(ch, x, look.y, on ? cycle(RAMPS.fire, ms, 70) : INK, 'left', look.scale);
    }
    if (on) {
      const c = blink(ms, 800) ? SELECTED : MUTED;
      screen.text('▲', x + w / 2, look.y - 11, c, 'center');
      screen.text('▼', x + w / 2, look.y + w + 5, c, 'center');
    }
  }
}

/** The last initials entered this session, offered first next time (Initials and the Daily board). */
let remembered = '';

export function rememberedInitials(): string {
  return remembered;
}

export function rememberInitials(letters: string): void {
  remembered = letters;
}
